import { useEffect, useMemo, useRef, useState } from "react";
import { format } from "date-fns";
import { useNavigate, Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft, Camera, X, MapPin, Clock, Users as UsersIcon, AlertTriangle,
  Wrench, Save, ChevronDown, ChevronUp, Check, FlagOff, Truck, UserPlus, Search,
  LogIn, LogOut,
} from "lucide-react";
import { supabase, apiFetch } from "@/lib/supabase";
import { useRequireAuth, hasRole } from "@/lib/auth";
import { Button, Card, Field, Input, Select, Textarea, Spinner } from "@/components/ui";
import {
  ACCEPTED_DIARY_PHOTO_MIMES, distanceMeters, OFF_SITE_THRESHOLD_M,
  OUTAGE_CARRIERS, OUTAGE_TYPES_BY_CARRIER, WEATHER_CHIPS, type WeatherChip,
  kmhToKt, ktToKmh, windBand, WIND_BAND_META,
} from "./diaryHelpers";
// T-258 — multi-photo support
import DiaryPhotosField, { type StagedPhoto } from "./DiaryPhotosField";
import { uploadDiaryPhoto } from "./diaryPhotoUpload";
import { cachePhoto, queueItem } from "@/lib/offline";   // T-401

// One plant item on the diary day (mig 378 shape). Hours stays a string for the input.
type PlantItem = { key: string; type: string; supplier: string; hours: string; operator: string; booking_id: string | null };
const newPlantItem = (p: Partial<PlantItem> = {}): PlantItem => ({
  key: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, type: "", supplier: "", hours: "", operator: "", booking_id: null, ...p,
});

// =============================================================================
// NewDiaryEntryScreen — /capture/diary
// T-156 — Site Diary entry form.
//
// Field workflow:
//   1. Pick project (allocated for field/supervisor; all live for manager+).
//   2. Time on site (start/end).
//   3. Who on site (crew picker) + visitors free-text.
//   4. Conditions chips + temperature.
//   5. Delays + scope summary.
//   6. Outage info (collapsed by default — most days won't have one).
//   7. End-of-day site photo.
//   8. Submit — GPS captured at save, off_site computed against site coords.
//
// On save, INSERT to site_diary_entries. Photo uploads to Supabase Storage
// 'site-diary-photos' bucket using the same pattern as licence-documents.
// First entry per project auto-stamps projects.actual_start_date via the
// migration trigger; "Job finished" tick on save stamps actual_end_date.
// =============================================================================

// T-258 — MAX_PHOTO_BYTES moved into DiaryPhotosField.

interface ProjectOption {
  project_id:    string;
  project_code:  string;
  project_name:  string;
  status:        string;
  site_lat:      number | null;
  site_lng:      number | null;
}

interface CrewOption {
  employee_id:      string;
  first_name:       string;
  last_name:        string;
  mobile:           string | null;
  employment_type:  string | null;
  end_date:         string | null;
}

interface OnSiteCrew  { employee_id: string; name: string; mobile?: string | null; }
interface OnSiteVisit {
  // "external" = free-text random visitor; "contact" = picked from internal/subbie contacts
  kind:        "external" | "contact";
  name:        string;
  company:     string;
  phone:       string;
  employee_id?: string | null;   // populated when kind=contact
}

export default function NewDiaryEntryScreen() {
  const user = useRequireAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();

  // -----------------------------------------------------------------------------
  // Form state
  // -----------------------------------------------------------------------------
  const [projectId,         setProjectId]    = useState("");
  // T-354 — entry date is editable so a diary can be logged after the fact
  // (you won't always remember on the day). Defaults to today; can't be future.
  // local day key, NOT toISOString — UTC gives YESTERDAY before 10am in QLD
  const [entryDate,         setEntryDate]    = useState(format(new Date(), "yyyy-MM-dd"));
  // Time on site uses HH:MM time-only inputs — date is always today, no
  // datetime-local picker (those are clunky on phones). At save time we
  // combine today's date + the time into a full ISO timestamp.
  const [startedTime,       setStartedTime]  = useState("");   // HH:MM
  const [endedTime,         setEndedTime]    = useState("");   // HH:MM
  const [crewSelected,      setCrewSelected] = useState<OnSiteCrew[]>([]);
  const [visitorInput,      setVisitorInput] = useState("");
  const [visitorCompany,    setVisitorCompany] = useState("");
  const [visitorPhone,      setVisitorPhone]   = useState("");
  const [visitors,          setVisitors]    = useState<OnSiteVisit[]>([]);
  const [contactPickerOpen, setContactPickerOpen] = useState(false);
  const [contactPickerQ,    setContactPickerQ] = useState("");
  const [pickerTab,         setPickerTab] = useState<"team" | "clients" | "subbies">("team"); // T-360 + T-381
  const [weather,           setWeather]      = useState<Set<WeatherChip>>(new Set());
  const [temperatureC,      setTemperatureC] = useState("");
  // T-373 — dual-unit wind speed. Filling either fills the other (linked).
  const [windKmh,           setWindKmh]      = useState("");
  const [windKt,            setWindKt]       = useState("");
  const [plantOnSite,       setPlantOnSite]  = useState(false);
  // 16 Sep 2026 — James, Beechmont: "EWP and a crane on the 14th, both were
  // visible on the site diary entry but you could only select one." Plant is
  // a LIST now: each item is type / supplier / hours / operator (free text,
  // or filled from a booking). Stored as conditions.plant[] with the old
  // single keys kept = the first item, so older readers still show one.
  const [plantItems,        setPlantItems]   = useState<PlantItem[]>([]);
  const [delays,            setDelays]       = useState("");
  const [delayBillable,     setDelayBillable] = useState(false);
  // T-327 — quick delay scratch-calc (labour / plant / TA). Informal: just
  // multiplies what Josh types; stored as delay_calc jsonb. No $ rates yet.
  const [delayReason,       setDelayReason]   = useState("");
  const [dCrew,             setDCrew]         = useState("");
  const [dLabourHrs,        setDLabourHrs]    = useState("");
  const [dPlantQty,         setDPlantQty]     = useState("");
  const [dPlantHrs,         setDPlantHrs]     = useState("");
  const [dTaQty,            setDTaQty]        = useState("");
  const [dTaNights,         setDTaNights]     = useState("");
  const [scopeDone,         setScopeDone]    = useState("");
  const [outageOpen,        setOutageOpen]   = useState(false);
  const [outageCarrier,     setOutageCarrier] = useState("");
  const [outageType,        setOutageType]   = useState("");
  const [outageRef,         setOutageRef]    = useState("");
  const [outageStartedAt,   setOutageStartedAt] = useState("");
  const [outageEndedAt,     setOutageEndedAt] = useState("");
  const [outageNotes,       setOutageNotes]  = useState("");
  // T-258 — multi-photo. Files are STAGED here; uploaded to R2 + inserted
  // into site_diary_photos AFTER the parent entry insert (diary_id needed).
  const [stagedPhotos, setStagedPhotos] = useState<StagedPhoto[]>([]);
  const [isJobFinished,     setIsJobFinished] = useState(false);
  const [submitError,       setSubmitError]   = useState<string | null>(null);

  // GPS state — captured on mount, refreshed on demand
  const [coords, setCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [coordsErr, setCoordsErr] = useState<string | null>(null);

  // Clocking only makes sense for a live day — a back-dated write-up types
  // its times. (Also used by the off-site GPS check on save.)
  const isTodayEntry = entryDate === new Date().toISOString().slice(0, 10);

  // T-432 — clock in/out stamps (Josh). Null = not clocked, times were typed.
  type ClockStamp = { iso: string; time: string; lat: number | null; lng: number | null };
  const [clockIn,  setClockIn]  = useState<ClockStamp | null>(null);
  const [clockOut, setClockOut] = useState<ClockStamp | null>(null);

  // One tap = the real moment, plus GPS IF the device gives it up quickly.
  // Location must never block or delay the stamp — a clock-in with no GPS is
  // still infinitely better than a time guessed at the end of the day.
  async function stampClock(which: "in" | "out") {
    const now = new Date();
    const hh = String(now.getHours()).padStart(2, "0");
    const mm = String(now.getMinutes()).padStart(2, "0");
    const time = `${hh}:${mm}`;

    const fix = await new Promise<{ lat: number; lng: number } | null>(resolve => {
      if (!navigator.geolocation) return resolve(null);
      navigator.geolocation.getCurrentPosition(
        p => resolve({ lat: p.coords.latitude, lng: p.coords.longitude }),
        () => resolve(null),
        { enableHighAccuracy: true, timeout: 8_000, maximumAge: 30_000 },
      );
    });

    const stamp: ClockStamp = { iso: now.toISOString(), time, lat: fix?.lat ?? null, lng: fix?.lng ?? null };
    if (which === "in") { setClockIn(stamp); setStartedTime(time); }
    else                { setClockOut(stamp); setEndedTime(time); }
    // Opportunistically refresh the entry-level GPS too if we didn't have one.
    if (fix && !coords) setCoords(fix);
  }

  const hoursBetween = (aIso: string, bIso: string) =>
    (Math.max(0, new Date(bIso).getTime() - new Date(aIso).getTime()) / 3_600_000).toFixed(2).replace(/\.00$/, "");

  // T-258 — fileInputRef removed (DiaryPhotosField owns its own inputs).

  // -----------------------------------------------------------------------------
  // GPS capture on mount
  // -----------------------------------------------------------------------------
  useEffect(() => {
    if (!navigator.geolocation) {
      setCoordsErr("This device doesn't support geolocation. Entry will be saved without GPS.");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      pos => setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      err => setCoordsErr(`Couldn't get your location (${err.message}). Entry will be saved without GPS.`),
      { enableHighAccuracy: true, timeout: 10_000, maximumAge: 60_000 },
    );
  }, []);

  // -----------------------------------------------------------------------------
  // Default to "now" for start time on first mount (HH:MM only).
  // -----------------------------------------------------------------------------
  useEffect(() => {
    if (!startedTime) {
      const now = new Date();
      const hh = String(now.getHours()).padStart(2, "0");
      const mm = String(now.getMinutes()).padStart(2, "0");
      setStartedTime(`${hh}:${mm}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // -----------------------------------------------------------------------------
  // Project options — RLS handles the field-side filtering. Manager+ sees all
  // active. Field/supervisor sees only what they can via app.can_see_project.
  // -----------------------------------------------------------------------------
  const isManagerPlus = hasRole(user, "manager");
  const { data: projects = [], isLoading: projectsLoading } = useQuery({
    queryKey: ["diary_project_options", isManagerPlus, user?.user_id],
    enabled: !!user,
    queryFn: async () => {
      let q = supabase
        .from("projects")
        .select(`
          project_id, project_code, project_name, status,
          sites:site_id ( latitude, longitude )
        `)
        // Only actively-worked jobs so the picker stays short (T-358b). Excludes
        // acceptance_pending, scheduled, on_hold, legacy, won, etc. If you need
        // to log against an acceptance_pending job (e.g. a re-visit to close a
        // defect), flip it back to live on the project first. RLS still allows
        // any open project — this is just the convenience list.
        .in("status", ["live", "mobilising"])
        .order("project_code");
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []).map((p: any): ProjectOption => ({
        project_id:   p.project_id,
        project_code: p.project_code,
        project_name: p.project_name,
        status:       p.status,
        site_lat:     p.sites?.latitude ?? null,
        site_lng:     p.sites?.longitude ?? null,
      }));
    },
  });

  // -----------------------------------------------------------------------------
  // Crew options — internal employees the user can pick to mark "on site".
  // Could narrow to project crew later; v0 lists all active employees.
  // -----------------------------------------------------------------------------
  const { data: crewOptions = [] } = useQuery({
    queryKey: ["diary_crew_options"],
    queryFn: async () => {
      // Pull current employees (any employment_type) — full-time, casual,
      // subcontractor, labour-hire, director. Filter only by employment
      // currency via end_date (null = still employed). Mixed list so the
      // contact picker can quick-add subbies AND internal staff.
      const today = new Date().toISOString().slice(0, 10);
      const { data, error } = await supabase
        .from("employees")
        .select("employee_id, first_name, last_name, mobile, employment_type, end_date")
        .or(`end_date.is.null,end_date.gte.${today}`)
        .order("last_name");
      if (error) throw error;
      return (data ?? []) as CrewOption[];
    },
  });

  // T-258 follow-up — auto-include the creator in the crew on first load.
  // If you're filling in the diary you were almost certainly on site, so
  // pre-tick yourself. The user can still untick if they were actually doing
  // the entry from the office. Only runs ONCE on initial load (gated on
  // crewSelected.length === 0) so we don't fight the user's manual removes.
  useEffect(() => {
    if (!user?.employee_id) return;
    if (crewOptions.length === 0) return;
    if (crewSelected.length > 0) return;
    const me = crewOptions.find(c => c.employee_id === user.employee_id);
    if (!me) return;
    setCrewSelected([{
      employee_id: me.employee_id,
      name:        `${me.first_name} ${me.last_name}`,
      mobile:      me.mobile,
    }]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.employee_id, crewOptions.length]);

  // Filtered list for the contact picker (visitors-from-contacts).
  // Excludes anyone already picked as crew (different concept — crew is "your
  // own people" on site; contacts is a quick-add for subbies / others).
  const contactPickerList = useMemo(() => {
    const q = contactPickerQ.trim().toLowerCase();
    const alreadyCrewIds = new Set(crewSelected.map(c => c.employee_id));
    const alreadyVisitorIds = new Set(visitors.filter(v => v.employee_id).map(v => v.employee_id));
    return crewOptions
      .filter(c => !alreadyCrewIds.has(c.employee_id) && !alreadyVisitorIds.has(c.employee_id))
      .filter(c => !q
        || `${c.first_name} ${c.last_name}`.toLowerCase().includes(q)
        || (c.mobile ?? "").includes(q));
  }, [crewOptions, contactPickerQ, crewSelected, visitors]);

  // -----------------------------------------------------------------------------
  // T-258 — Photo lifecycle moved into DiaryPhotosField (owns preview URLs +
  // file validation). Parent only sees the staged list via stagedPhotos state.
  // -----------------------------------------------------------------------------

  // -----------------------------------------------------------------------------
  // Visitor add/remove
  // -----------------------------------------------------------------------------
  function addVisitorFreeText() {
    const name = visitorInput.trim();
    if (!name) return;
    setVisitors(prev => [...prev, {
      kind:    "external",
      name,
      company: visitorCompany.trim(),
      phone:   visitorPhone.trim(),
    }]);
    setVisitorInput("");
    setVisitorCompany("");
    setVisitorPhone("");
  }

  function addVisitorFromContact(emp: CrewOption) {
    const isSubbie = emp.employment_type === "subcontractor";
    setVisitors(prev => [...prev, {
      kind:        "contact",
      name:        `${emp.first_name} ${emp.last_name}`,
      company:     isSubbie ? "Subcontractor" : "Coretel",
      phone:       emp.mobile ?? "",
      employee_id: emp.employee_id,
    }]);
    setContactPickerQ("");
    // Keep picker open for multi-add; user closes when done
  }

  // T-360 — saved CLIENT contacts (Ventia FM etc.) so on-site client reps can be
  // added without re-typing. Pulled from client_contacts + the client's name.
  const { data: clientContacts = [] } = useQuery({
    queryKey: ["diary_client_contacts"],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("client_contacts")
        .select("contact_id, full_name, role_title, phone, client:client_id(legal_name, client_code)")
        .eq("is_active", true)
        .order("full_name");
      if (error) throw error;
      return (data ?? []) as any[];
    },
  });
  const clientContactList = useMemo(() => {
    const q = contactPickerQ.trim().toLowerCase();
    return (clientContacts as any[]).filter(c => !q
      || (c.full_name ?? "").toLowerCase().includes(q)
      || (c.role_title ?? "").toLowerCase().includes(q)
      || (c.client?.legal_name ?? "").toLowerCase().includes(q)
      || (c.phone ?? "").includes(q));
  }, [clientContacts, contactPickerQ]);

  // T-381 — subbie WORKERS (Ramsamy's crew etc) from the subbie hub, pickable
  // as on-site people. Read-only names/roles (RLS: mig 222).
  const { data: subbieWorkers = [] } = useQuery({
    queryKey: ["diary_subbie_workers"],
    enabled: contactPickerOpen,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("subbie_workers")
        .select("worker_id, first_name, last_name, job_title, mobile, company:subcontractor_company_id(trading_name, legal_name)")
        .eq("is_active", true)
        .order("last_name");
      if (error) throw error;
      return (data ?? []) as any[];
    },
  });
  const subbieWorkerList = useMemo(() => {
    const q = contactPickerQ.trim().toLowerCase();
    const already = new Set(visitors.map(v => v.name));
    return (subbieWorkers as any[]).filter(w => {
      const name = `${w.first_name} ${w.last_name}`;
      if (already.has(name)) return false;
      if (!q) return true;
      return name.toLowerCase().includes(q)
        || (w.job_title ?? "").toLowerCase().includes(q)
        || (w.company?.trading_name ?? w.company?.legal_name ?? "").toLowerCase().includes(q);
    });
  }, [subbieWorkers, contactPickerQ, visitors]);
  function addVisitorFromSubbieWorker(w: any) {
    setVisitors(prev => [...prev, {
      kind:    "external",
      name:    `${w.first_name} ${w.last_name}`,
      company: w.company?.trading_name ?? w.company?.legal_name ?? "Subcontractor",
      phone:   w.mobile ?? "",
    } as any]);
  }
  function addVisitorFromClientContact(cc: any) {
    setVisitors(prev => [...prev, {
      kind:    "external",
      name:    cc.full_name,
      company: cc.client?.legal_name ?? "",
      phone:   cc.phone ?? "",
    }]);
    setContactPickerQ("");
  }

  // -----------------------------------------------------------------------------
  // Crew toggle
  // -----------------------------------------------------------------------------
  function toggleCrew(emp: CrewOption) {
    setCrewSelected(prev => {
      const exists = prev.find(p => p.employee_id === emp.employee_id);
      if (exists) return prev.filter(p => p.employee_id !== emp.employee_id);
      return [...prev, {
        employee_id: emp.employee_id,
        name:        `${emp.first_name} ${emp.last_name}`,
        mobile:      emp.mobile,
      }];
    });
  }

  const selectedProject = useMemo(
    () => projects.find(p => p.project_id === projectId) ?? null,
    [projects, projectId],
  );

  // off_site computed at submit time — preview here for the user's awareness
  const offSitePreview = useMemo(() => {
    if (!coords || !selectedProject?.site_lat || !selectedProject?.site_lng) return null;
    const m = distanceMeters(coords.lat, coords.lng, selectedProject.site_lat, selectedProject.site_lng);
    return { meters: m, isOff: m > OFF_SITE_THRESHOLD_M };
  }, [coords, selectedProject]);

  // -----------------------------------------------------------------------------
  // Submit flow:
  //   1. Generate diary_id client-side (uuid).
  //   2. If photo selected → ask worker for a presigned PUT URL → PUT to R2.
  //   3. INSERT site_diary_entries with diary_id + photo_storage_path baked
  //      in (single transaction; no separate patch step).
  // Photos live in R2 coretel-prod under diary/<project_id>/<diary_id>/...
  // -----------------------------------------------------------------------------
  const submitMut = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error("Not signed in.");
      if (!projectId) throw new Error("Pick a project first.");

      const diaryId = crypto.randomUUID();

      // Derive off_site flag at submit time. Only meaningful for a SAME-DAY log —
      // a back-dated entry is written from wherever you are now (home/office), so
      // the GPS says nothing about the site; skip it. Also clamp the distance so a
      // bad/placeholder site coordinate (giant distance) can't overflow
      // off_site_distance_m (numeric(8,1), max 9,999,999.9 m) and block the save.
      let offSite = false;
      let offSiteDistanceM: number | null = null;
      if (isTodayEntry && coords && selectedProject?.site_lat && selectedProject?.site_lng) {
        const raw = distanceMeters(
          coords.lat, coords.lng,
          selectedProject.site_lat, selectedProject.site_lng,
        );
        offSite = raw > OFF_SITE_THRESHOLD_M;
        // Beyond ~10,000 km the site coords are almost certainly wrong — keep the
        // flag but don't store a bogus (overflowing) distance.
        offSiteDistanceM = raw <= 9_999_999 ? Math.round(raw * 10) / 10 : null;
      }

      // T-258 — multi-photo. Single-photo upload removed. Photos are uploaded
      // AFTER the entry insert so we have diary_id for the site_diary_photos
      // rows. photoKey stays null on the parent entry (the legacy column is
      // kept for backwards compat but we don't write to it anymore).
      const photoKey: string | null = null;

      // 2. Build payload + INSERT.
      const outage = (outageOpen && outageCarrier) ? {
        carrier:    outageCarrier,
        type:       outageType || null,
        ref:        outageRef.trim() || null,
        started_at: outageStartedAt || null,
        ended_at:   outageEndedAt || null,
        notes:      outageNotes.trim() || null,
      } : null;

      const conditions: any = {};
      if (weather.size > 0)        conditions.weather = Array.from(weather);
      if (temperatureC)            conditions.temperature_c = Number(temperatureC);
      if (windKmh) {               // T-373 — store both units (km/h canonical)
        conditions.wind_kmh = Number(windKmh);
        conditions.wind_kt  = windKt ? Number(windKt) : kmhToKt(Number(windKmh));
      }
      if (plantOnSite) {
        conditions.plant_on_site = true;
        // mig 378 — the list, minus empty rows
        const items = plantItems
          .map(p => ({ type: p.type.trim(), supplier: p.supplier.trim(), hours: p.hours.trim(), operator: p.operator.trim(), booking_id: p.booking_id }))
          .filter(p => p.type || p.supplier || p.hours || p.operator);
        if (items.length) {
          conditions.plant = items.map(p => ({
            type: p.type, supplier: p.supplier, hours: p.hours, operator: p.operator,
            ...(p.booking_id ? { booking_id: p.booking_id } : {}),
          }));
          // old single keys = first item, for readers that only know one plant
          const first = items[0];
          if (first.type)     conditions.plant_type     = first.type;
          if (first.supplier) conditions.plant_supplier = first.supplier;
          if (first.hours)    conditions.plant_hours    = Number(first.hours);
          if (first.operator) conditions.plant_operator = first.operator;
        }
      }

      // Compose started_at / ended_at as full ISO timestamps using the ENTRY
      // date (today by default, or a back-dated day) + the HH:MM time inputs.
      const baseDate = new Date(entryDate + "T00:00:00");
      const composeTs = (hhmm: string): string | null => {
        if (!hhmm) return null;
        const [h, m] = hhmm.split(":");
        const local = new Date(baseDate);
        local.setHours(Number(h), Number(m), 0, 0);
        return local.toISOString();
      };
      // T-432 — a clocked time is the literal instant of the tap; a typed one
      // is composed against the entry date. Clock wins where present.
      const startedAtIso = clockIn?.iso  ?? composeTs(startedTime);
      const endedAtIso   = clockOut?.iso ?? composeTs(endedTime);

      const whoOnSite = {
        crew:     crewSelected,
        visitors: visitors,
      };

      // Check: is this the first entry on this project?
      // T-401 — offline-tolerant: if we can't reach the DB, assume not-first
      // (cosmetic flag only) rather than failing the whole save.
      let isFirstEntry = false;
      try {
        const { count: priorCount } = await supabase
          .from("site_diary_entries")
          .select("diary_id", { count: "exact", head: true })
          .eq("project_id", projectId);
        isFirstEntry = (priorCount ?? 0) === 0;
      } catch { /* offline — leave false */ }

      // T-327 — assemble the delay scratch-calc (only when a delay is recorded)
      const dnum = (s: string) => { const v = parseFloat(s); return isNaN(v) ? 0 : v; };
      const delayCalc = delays.trim() ? {
        reason: delayReason || null,
        ...((dnum(dCrew) || dnum(dLabourHrs)) ? { labour: { crew: dnum(dCrew), hours: dnum(dLabourHrs) } } : {}),
        ...((dnum(dPlantQty) || dnum(dPlantHrs)) ? { plant: { qty: dnum(dPlantQty), hours: dnum(dPlantHrs) } } : {}),
        ...((dnum(dTaQty) || dnum(dTaNights)) ? { ta: { qty: dnum(dTaQty), nights: dnum(dTaNights) } } : {}),
      } : null;

      const insertPayload: any = {
        diary_id:      diaryId,
        project_id:    projectId,
        entry_date:    entryDate,
        who_on_site:   whoOnSite,
        started_at:    startedAtIso,
        ended_at:      endedAtIso,
        conditions,
        delays:        delays.trim() || null,
        delay_billable: delayBillable,
        delay_calc:    delayCalc,
        scope_done:    scopeDone.trim() || null,
        outage,
        photo_storage_path: photoKey,
        lat:           coords?.lat ?? null,
        lng:           coords?.lng ?? null,
        // T-432 — capture provenance + per-stamp GPS (mig 251)
        clock_in_method:  startedAtIso ? (clockIn  ? "clocked" : "manual") : null,
        clock_out_method: endedAtIso   ? (clockOut ? "clocked" : "manual") : null,
        clocked_in_at:  clockIn?.iso  ?? null,
        clocked_out_at: clockOut?.iso ?? null,
        clock_in_lat:   clockIn?.lat  ?? null,
        clock_in_lng:   clockIn?.lng  ?? null,
        clock_out_lat:  clockOut?.lat ?? null,
        clock_out_lng:  clockOut?.lng ?? null,
        off_site:      offSite,
        off_site_distance_m: offSiteDistanceM,
        is_first_entry: isFirstEntry,
        is_job_finished: isJobFinished,
        created_by:    user.user_id,
      };

      // T-401 — cache staged photos + queue the whole entry for background
      // sync. Used when offline from the start, when the entry insert fails
      // on flaky signal, or for individual photos that fail mid-loop. The
      // replay (lib/offline.ts) is idempotent: diary_id is client-minted so
      // the entry insert dup-tolerates, and each photo's cache is removed on
      // success so partial replays resume without duplicating.
      const queueEntry = async (photoIdxs: number[]) => {
        const photos = [];
        for (const i of photoIdxs) {
          const sp = stagedPhotos[i];
          const local_id = `diary_${diaryId}_${i}`;
          await cachePhoto(local_id, sp.file, { file_name: sp.file.name });
          photos.push({
            local_id,
            file_name:       sp.file.name,
            content_type:    sp.file.type,
            caption:         sp.caption.trim() || null,
            kind:            sp.kind || "general",
            sort_order:      i,
            precomputed_gps: null,
            skip_watermark:  sp.watermark === false,
          });
        }
        const { diary_id: _omit, ...entryFields } = insertPayload;
        await queueItem("diary_entry", { diary_id: diaryId, entry: entryFields, photos });
      };

      if (!navigator.onLine) {
        await queueEntry(stagedPhotos.map((_, i) => i));
        return { diaryId, queued: true };
      }

      try {
        const { error: insErr } = await supabase
          .from("site_diary_entries")
          .insert(insertPayload);
        if (insErr) throw insErr;
      } catch (insFail) {
        // Flaky mid-save — nothing is lost, the whole entry syncs later.
        console.warn("[T-401] diary insert failed — queued for sync", insFail);
        await queueEntry(stagedPhotos.map((_, i) => i));
        return { diaryId, queued: true };
      }

      // T-258 — upload staged photos AFTER the parent insert. T-401: a photo
      // that fails no longer just logs a warning — it's cached + queued so
      // the sync loop finishes the job.
      const failedIdxs: number[] = [];
      for (let i = 0; i < stagedPhotos.length; i++) {
        const sp = stagedPhotos[i];
        try {
          await uploadDiaryPhoto({
            diaryId:    diaryId,
            projectId:  projectId,
            file:       sp.file,
            caption:    sp.caption.trim() || null,
            kind:       sp.kind || "general",
            sortOrder:  i,
            // T-366 — honour the per-entry watermark toggle (default on).
            skipWatermark: sp.watermark === false,
            precomputedGps: null,
          });
        } catch (photoErr) {
          console.warn(`[T-258/T-401] photo ${i + 1}/${stagedPhotos.length} failed — queuing:`, photoErr);
          failedIdxs.push(i);
        }
      }
      if (failedIdxs.length > 0) {
        await queueEntry(failedIdxs);   // entry re-insert dup-tolerates on replay
        return { diaryId, queued: true, partialPhotos: failedIdxs.length };
      }

      return { diaryId, queued: false };
    },
    onSuccess: (res: any) => {
      qc.invalidateQueries({ queryKey: ["project_site_diary"] });
      qc.invalidateQueries({ queryKey: ["project", projectId] });
      if (res?.queued) {
        alert(res.partialPhotos
          ? `Entry saved — ${res.partialPhotos} photo${res.partialPhotos === 1 ? "" : "s"} couldn't upload right now and will sync automatically when signal improves.`
          : "No signal — your diary entry is saved on this phone and will upload itself when you're back in coverage.");
      }
      // Send the user to the project's diary tab so they see their entry
      navigate(`/projects/${projectId}?tab=diary`);
    },
    onError: (err: any) => {
      setSubmitError(err?.message ?? "Submission failed — please try again.");
    },
  });

  // -----------------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------------
  if (!user) return null;

  return (
    <div className="p-4 max-w-3xl mx-auto pb-32">
      <Link to="/capture" className="text-text-muted text-sm flex items-center gap-1 hover:text-text">
        <ArrowLeft size={14}/> Back
      </Link>
      <h1 className="text-2xl font-semibold mt-2">Site diary entry</h1>
      <p className="text-sm text-text-muted mt-1">
        Quick log of today's work. GPS, time, and your name are captured automatically.
      </p>

      {/* Project picker */}
      <Card className="mt-4">
        <div className="p-4 space-y-3">
          <Field label="Project *">
            {projectsLoading ? (
              <div className="flex items-center gap-2 text-sm text-text-muted">
                <Spinner size={14}/> Loading projects…
              </div>
            ) : projects.length === 0 ? (
              <div className="text-sm text-text-muted">
                No active projects you can log against right now. Ask a manager to assign you.
              </div>
            ) : (
              <Select value={projectId} onChange={e => setProjectId(e.target.value)}>
                <option value="">— Select —</option>
                {projects.map(p => (
                  <option key={p.project_id} value={p.project_id}>
                    {p.project_code} · {p.project_name}{p.status !== "live" ? ` (${p.status})` : ""}
                  </option>
                ))}
              </Select>
            )}
          </Field>

          {/* GPS state line */}
          <div className="text-[11px] flex items-center gap-2 text-text-muted">
            <MapPin size={11}/>
            {coords
              ? <>GPS captured · {coords.lat.toFixed(5)}, {coords.lng.toFixed(5)}</>
              : coordsErr
                ? <span className="text-warn">{coordsErr}</span>
                : <span>Getting GPS…</span>}
            {offSitePreview && offSitePreview.isOff && (
              <span className="ml-1 inline-flex items-center gap-1 text-warn font-medium">
                · Off-site by {Math.round(offSitePreview.meters)} m (will be flagged)
              </span>
            )}
          </div>
        </div>
      </Card>

      {/* Time on site — entry date (defaults today, can back-date) + time inputs. */}
      <Card className="mt-3">
        <div className="p-4">
          <SectionTitle icon={Clock}>Time on site</SectionTitle>
          <Field label="Entry date" hint="Defaults to today. Back-date it if you're logging after the fact.">
            <Input
              type="date"
              value={entryDate}
              max={new Date().toISOString().slice(0, 10)}
              onChange={e => setEntryDate(e.target.value)}
            />
          </Field>
          {entryDate !== new Date().toISOString().slice(0, 10) && (
            <div className="text-[11px] text-warn mt-1">Back-dated entry — logging for {entryDate}.</div>
          )}
          <div className="grid grid-cols-2 gap-3 mt-3">
            <Field label="Started">
              <Input type="time" value={startedTime}
                onChange={e => { setStartedTime(e.target.value); setClockIn(null); }} />
            </Field>
            <Field label="Ended">
              <Input type="time" value={endedTime}
                onChange={e => { setEndedTime(e.target.value); setClockOut(null); }} />
            </Field>
          </div>

          {/* T-432 — Josh's clock in/out. Optional: one tap stamps the real
              time (+ GPS if allowed) instead of the crew guessing at knock-off.
              Typing in the boxes above clears the stamp so we never claim a
              time was clocked when it was edited. */}
          {isTodayEntry && (
            <div className="mt-3 rounded-md border border-line bg-ink-raised/40 p-3">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="text-[11px] text-text-muted">
                  Clock on/off for real times — optional, or just type them above.
                </div>
                <div className="flex gap-2">
                  <Button type="button" size="sm" variant={clockIn ? "secondary" : "primary"}
                    icon={LogIn} disabled={!!clockIn}
                    onClick={() => void stampClock("in")}>
                    {clockIn ? `On ${clockIn.time}` : "Clock in"}
                  </Button>
                  <Button type="button" size="sm" variant="secondary"
                    icon={LogOut} disabled={!clockIn || !!clockOut}
                    onClick={() => void stampClock("out")}>
                    {clockOut ? `Off ${clockOut.time}` : "Clock out"}
                  </Button>
                </div>
              </div>
              {(clockIn || clockOut) && (
                <div className="text-[11px] text-ok mt-2 flex flex-wrap gap-x-3 gap-y-0.5">
                  {clockIn && (
                    <span>✓ On site {clockIn.time}{clockIn.lat != null ? " · GPS" : " · no GPS"}</span>
                  )}
                  {clockOut && (
                    <span>✓ Off site {clockOut.time}{clockOut.lat != null ? " · GPS" : " · no GPS"}
                      {clockIn ? ` · ${hoursBetween(clockIn.iso, clockOut.iso)}h on site` : ""}</span>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </Card>

      {/* Who on site */}
      <Card className="mt-3">
        <div className="p-4">
          <SectionTitle icon={UsersIcon}>Who was on site</SectionTitle>
          <div className="text-[11px] text-text-muted mt-1 mb-2">Tap to add. You can pick multiple.</div>
          <div className="flex flex-wrap gap-1.5">
            {crewOptions.map(c => {
              const isOn = !!crewSelected.find(s => s.employee_id === c.employee_id);
              return (
                <button
                  key={c.employee_id}
                  type="button"
                  onClick={() => toggleCrew(c)}
                  className={`px-2.5 py-1.5 rounded-full text-[12px] border transition-colors ${
                    isOn
                      ? "bg-accent text-ink border-accent"
                      : "bg-ink-raised text-text border-line hover:border-accent"
                  }`}
                >
                  {isOn && <Check size={11} className="inline mr-1 -mt-0.5"/>}
                  {c.first_name} {c.last_name}
                </button>
              );
            })}
          </div>

          {/* Visitors — two paths: pick from known contacts, OR free-text. */}
          <div className="mt-4 pt-3 border-t border-line/50">
            <div className="flex items-center justify-between mb-1.5">
              <div className="text-[11px] uppercase tracking-wider2 text-text-muted">
                Visitors / outsiders on site
              </div>
              <button
                type="button"
                onClick={() => setContactPickerOpen(true)}
                className="text-[11px] text-accent hover:underline inline-flex items-center gap-1"
              >
                <UserPlus size={11}/> Pick from contacts
              </button>
            </div>

            {/* Free-text visitor add: name / company / phone / button */}
            <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_1fr_auto] gap-2 items-end">
              <Input
                placeholder="Name"
                value={visitorInput}
                onChange={e => setVisitorInput(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addVisitorFreeText(); } }}
              />
              <Input
                placeholder="Company"
                value={visitorCompany}
                onChange={e => setVisitorCompany(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addVisitorFreeText(); } }}
              />
              <Input
                placeholder="Phone"
                inputMode="tel"
                value={visitorPhone}
                onChange={e => setVisitorPhone(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addVisitorFreeText(); } }}
              />
              <Button size="sm" variant="secondary" onClick={addVisitorFreeText} disabled={!visitorInput.trim()}>
                Add
              </Button>
            </div>

            {visitors.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {visitors.map((v, i) => (
                  <span key={i} className="inline-flex items-center gap-1 px-2 py-1 bg-ink-raised border border-line rounded-full text-[11px]">
                    {v.kind === "contact" && <Check size={10} className="text-accent"/>}
                    {v.name}
                    {v.company && <span className="text-text-muted">· {v.company}</span>}
                    {v.phone && <span className="text-text-muted">· {v.phone}</span>}
                    <button
                      type="button"
                      onClick={() => setVisitors(prev => prev.filter((_, ix) => ix !== i))}
                      className="ml-1 hover:text-lost"
                    ><X size={10}/></button>
                  </span>
                ))}
              </div>
            )}

            {/* Contact picker overlay — choose from internal employees +
                subcontractor employees. Auto-fills phone. Keep open after a
                pick so the user can multi-select before closing. */}
            {contactPickerOpen && (
              <>
                <div className="fixed inset-0 bg-black/40 z-40" onClick={() => setContactPickerOpen(false)}/>
                <div className="fixed inset-x-4 top-12 md:inset-auto md:top-20 md:left-1/2 md:-translate-x-1/2 md:w-96 z-50 bg-ink-card border border-line rounded-lg shadow-xl max-h-[70vh] flex flex-col">
                  <div className="p-3 border-b border-line flex items-center gap-2">
                    <Search size={14} className="text-text-muted"/>
                    <input
                      autoFocus
                      placeholder={pickerTab === "clients" ? "Find a client contact…" : "Find a teammate or subbie…"}
                      value={contactPickerQ}
                      onChange={e => setContactPickerQ(e.target.value)}
                      className="flex-1 bg-transparent text-[13px] focus:outline-none"
                    />
                    <button
                      onClick={() => setContactPickerOpen(false)}
                      className="text-text-muted hover:text-text"
                    ><X size={16}/></button>
                  </div>
                  {/* T-360 + T-381 — Team / Subbie crews (from the hub) / Client contacts */}
                  <div className="flex items-center gap-1 px-3 py-2 border-b border-line">
                    {(["team", "subbies", "clients"] as const).map(t => (
                      <button key={t} onClick={() => setPickerTab(t)}
                        className={`px-3 py-1 rounded text-[12px] ${pickerTab === t ? "bg-accent/15 text-accent border border-accent/30" : "text-text-muted border border-transparent hover:text-text"}`}>
                        {t === "team" ? "Team" : t === "subbies" ? "Subbie crews" : "Client contacts"}
                      </button>
                    ))}
                  </div>
                  <div className="flex-1 overflow-y-auto">
                    {pickerTab === "subbies" ? (
                      subbieWorkerList.length === 0 ? (
                        <div className="p-6 text-center text-[12px] text-text-muted">
                          {contactPickerQ ? "No matching subbie workers." : "No subbie workers recorded — add them on the subbie's page under Finance › Subcontractors."}
                        </div>
                      ) : subbieWorkerList.map((w: any) => (
                        <button
                          key={w.worker_id}
                          onClick={() => addVisitorFromSubbieWorker(w)}
                          className="w-full text-left px-3 py-2.5 hover:bg-ink-raised border-b border-line/30 last:border-b-0 flex items-center justify-between gap-2"
                        >
                          <div className="min-w-0">
                            <div className="text-[13px] font-medium">{w.first_name} {w.last_name}</div>
                            <div className="text-[11px] text-text-muted truncate">
                              {w.company?.trading_name ?? w.company?.legal_name ?? ""}{w.job_title ? ` · ${w.job_title}` : ""}
                            </div>
                          </div>
                          <span className="text-[10px] px-1.5 py-0.5 rounded font-mono uppercase bg-bid/15 text-bid">Subbie</span>
                        </button>
                      ))
                    ) : pickerTab === "team" ? (
                      contactPickerList.length === 0 ? (
                        <div className="p-6 text-center text-[12px] text-text-muted">
                          {contactPickerQ ? "No matches." : "Everyone you can add is already on site or in visitors."}
                        </div>
                      ) : contactPickerList.map(c => (
                        <button
                          key={c.employee_id}
                          onClick={() => addVisitorFromContact(c)}
                          className="w-full text-left px-3 py-2.5 hover:bg-ink-raised border-b border-line/30 last:border-b-0 flex items-center justify-between gap-2"
                        >
                          <div className="min-w-0">
                            <div className="text-[13px] font-medium">{c.first_name} {c.last_name}</div>
                            {c.mobile && <div className="text-[11px] text-text-muted font-mono">{c.mobile}</div>}
                          </div>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded font-mono uppercase ${
                            c.employment_type === "subcontractor"
                              ? "bg-bid/15 text-bid"
                              : "bg-ok/15 text-ok"
                          }`}>
                            {c.employment_type === "subcontractor" ? "Subbie" : "Coretel"}
                          </span>
                        </button>
                      ))
                    ) : (
                      clientContactList.length === 0 ? (
                        <div className="p-6 text-center text-[12px] text-text-muted">
                          {contactPickerQ ? "No matching client contacts." : "No saved client contacts. Add them on the Client's page."}
                        </div>
                      ) : clientContactList.map((c: any) => (
                        <button
                          key={c.contact_id}
                          onClick={() => addVisitorFromClientContact(c)}
                          className="w-full text-left px-3 py-2.5 hover:bg-ink-raised border-b border-line/30 last:border-b-0 flex items-center justify-between gap-2"
                        >
                          <div className="min-w-0">
                            <div className="text-[13px] font-medium">{c.full_name}</div>
                            <div className="text-[11px] text-text-muted truncate">
                              {c.client?.legal_name ?? ""}{c.role_title ? ` · ${c.role_title}` : ""}
                              {c.phone ? ` · ${c.phone}` : ""}
                            </div>
                          </div>
                          <span className="text-[10px] px-1.5 py-0.5 rounded font-mono uppercase bg-accent/15 text-accent">Client</span>
                        </button>
                      ))
                    )}
                  </div>
                  <div className="p-2 border-t border-line text-center">
                    <button
                      onClick={() => setContactPickerOpen(false)}
                      className="text-[12px] text-accent hover:underline"
                    >
                      Done ({visitors.filter(v => v.kind === "contact").length} added from contacts)
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </Card>

      {/* Conditions */}
      <Card className="mt-3">
        <div className="p-4">
          <SectionTitle>Conditions</SectionTitle>
          <div className="flex flex-wrap gap-1.5 mt-3">
            {WEATHER_CHIPS.map(w => {
              const on = weather.has(w.value);
              return (
                <button
                  key={w.value}
                  type="button"
                  onClick={() => setWeather(prev => {
                    const next = new Set(prev);
                    next.has(w.value) ? next.delete(w.value) : next.add(w.value);
                    return next;
                  })}
                  className={`px-2.5 py-1 rounded-full text-[12px] border transition-colors ${
                    on
                      ? "bg-accent/15 text-accent border-accent/40"
                      : "bg-ink-raised text-text border-line hover:border-accent"
                  }`}
                >
                  {w.label}
                </button>
              );
            })}
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <Field label="Temperature (°C, optional)" hint="">
              <Input
                type="number"
                inputMode="numeric"
                value={temperatureC}
                onChange={e => setTemperatureC(e.target.value)}
                placeholder="e.g. 28"
                className="max-w-[140px]"
              />
            </Field>
            {/* T-373 — dual-unit wind. Type either; the other auto-fills. */}
            <Field label="Wind (km/h)" hint="">
              <Input
                type="number"
                inputMode="numeric"
                value={windKmh}
                onChange={e => {
                  const v = e.target.value;
                  setWindKmh(v);
                  setWindKt(v === "" ? "" : String(kmhToKt(Number(v))));
                }}
                placeholder="e.g. 25"
                className="max-w-[110px]"
              />
            </Field>
            <Field label="Wind (kt)" hint="">
              <Input
                type="number"
                inputMode="numeric"
                value={windKt}
                onChange={e => {
                  const v = e.target.value;
                  setWindKt(v);
                  setWindKmh(v === "" ? "" : String(ktToKmh(Number(v))));
                }}
                placeholder="e.g. 13.5"
                className="max-w-[110px]"
              />
            </Field>
            {windKmh !== "" && !Number.isNaN(Number(windKmh)) && (() => {
              const band = windBand(Number(windKmh));
              const meta = WIND_BAND_META[band];
              return (
                <span className={`mb-2 px-2.5 py-1 rounded-full text-[12px] border ${meta.chipClass}`}>
                  {meta.label}
                </span>
              );
            })()}
          </div>
        </div>
      </Card>

      {/* Plant on site — a LIST of items (mig 378). Tap a booking to add it
          as a row, or "+ add plant" for unbooked gear; each row is Type +
          Supplier + Hours + Operator. */}
      <Card className="mt-3">
        <div className="p-4">
          <SectionTitle icon={Truck}>Plant on site</SectionTitle>
          <p className="text-[11px] text-text-muted mt-1 mb-3">
            EWPs, cranes, franna, trucks, generators — anything mobile that
            came on site today. Add one row per machine.
          </p>
          <label className="flex items-center gap-2 text-[13px] font-medium">
            <input
              type="checkbox"
              checked={plantOnSite}
              onChange={e => {
                setPlantOnSite(e.target.checked);
                if (e.target.checked && plantItems.length === 0) setPlantItems([newPlantItem()]);
              }}
              className="w-4 h-4"
            />
            Plant on site today
          </label>
          {/* booked plant near this date — tap one and it becomes a row (tap again to take it off) */}
          <PlantBookingSuggest
            projectId={projectId}
            entryDate={entryDate}
            pickedBookingIds={plantItems.map(p => p.booking_id).filter((x): x is string => !!x)}
            onPick={(p) => {
              setPlantOnSite(true);
              setPlantItems(prev => {
                if (prev.some(x => x.booking_id === p.booking_id)) return prev.filter(x => x.booking_id !== p.booking_id);
                const blank = prev.findIndex(x => !x.booking_id && !x.type && !x.supplier && !x.hours && !x.operator);
                const item = newPlantItem({ type: p.type, supplier: p.supplier, operator: p.operator, booking_id: p.booking_id });
                if (blank >= 0) return prev.map((x, i) => (i === blank ? item : x));
                return [...prev, item];
              });
            }}
          />
          {plantOnSite && (
            <div className="mt-3 space-y-3">
              {plantItems.map((p, idx) => (
                <div key={p.key} className="rounded-md border border-line bg-ink-raised p-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-mono">
                      Plant {idx + 1}{p.booking_id ? " · from the schedule" : ""}
                    </div>
                    <button type="button" onClick={() => setPlantItems(prev => prev.filter(x => x.key !== p.key))}
                      className="text-text-muted hover:text-danger p-1" title="Remove this plant">
                      <X size={13} />
                    </button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Field label="Type" hint="e.g. 20T Franna, EWP 18m, 5T Tipper">
                      <Input
                        value={p.type}
                        onChange={e => setPlantItems(prev => prev.map(x => x.key === p.key ? { ...x, type: e.target.value } : x))}
                        placeholder="Type of plant"
                      />
                    </Field>
                    <Field label="Supplier" hint="Hire company or 'Internal'">
                      <Input
                        value={p.supplier}
                        onChange={e => setPlantItems(prev => prev.map(x => x.key === p.key ? { ...x, supplier: e.target.value } : x))}
                        placeholder="e.g. Coates Hire"
                      />
                    </Field>
                    <Field label="Time on site (hours)">
                      <Input
                        type="number"
                        inputMode="decimal"
                        step="0.5"
                        min="0"
                        value={p.hours}
                        onChange={e => setPlantItems(prev => prev.map(x => x.key === p.key ? { ...x, hours: e.target.value } : x))}
                        placeholder="e.g. 4"
                      />
                    </Field>
                    <Field label="Operator" hint="Who's running it (employee or external)">
                      <Input
                        value={p.operator}
                        onChange={e => setPlantItems(prev => prev.map(x => x.key === p.key ? { ...x, operator: e.target.value } : x))}
                        placeholder="Name"
                      />
                    </Field>
                  </div>
                </div>
              ))}
              <button type="button" onClick={() => setPlantItems(prev => [...prev, newPlantItem()])}
                className="text-[12px] text-accent hover:underline">
                + add another plant
              </button>
            </div>
          )}
        </div>
      </Card>

      {/* Delays + scope */}
      <Card className="mt-3">
        <div className="p-4 space-y-3">
          <SectionTitle icon={Wrench}>Scope + delays</SectionTitle>
          {/* mig 358 — the day's program lines with their sub-activity ticks */}
          <TodaysBuildPlan projectId={projectId} entryDate={entryDate} canManage={isManagerPlus} />
          <Field label="What got done today">
            <Textarea
              rows={3}
              value={scopeDone}
              onChange={e => setScopeDone(e.target.value)}
              placeholder="Pre-assembled the headframe modules; cleared cable tray; set out earthing pit."
            />
          </Field>
          <Field label="Delays / blockers (if any)">
            <Textarea
              rows={2}
              value={delays}
              onChange={e => setDelays(e.target.value)}
              placeholder="Late arrival of crane crew; weather hold for 90 min."
            />
          </Field>
          {delays.trim() && (
            <div className="space-y-2.5 rounded-md border border-line bg-ink-raised p-3">
              <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-mono">Delay impact — quick calc</div>
              <Field label="Reason">
                <Select value={delayReason} onChange={e => setDelayReason(e.target.value)}>
                  <option value="">—</option>
                  <option value="weather">Weather</option>
                  <option value="client_access">Client / access</option>
                  <option value="materials">Materials</option>
                  <option value="design">Design issue</option>
                  <option value="plant_breakdown">Plant breakdown</option>
                  <option value="other">Other</option>
                </Select>
              </Field>
              {/* Labour: crew × hours lost */}
              <div className="flex items-end gap-2 flex-wrap text-[12px]">
                <span className="w-12 text-text">Labour</span>
                <label className="w-[72px]"><span className="text-[10px] text-text-muted block">Crew</span>
                  <input type="number" min={0} inputMode="numeric" value={dCrew} onChange={e => setDCrew(e.target.value)} className="w-full bg-ink border border-line rounded px-2 py-1 font-mono"/></label>
                <span className="pb-1.5 text-text-muted">×</span>
                <label className="w-[72px]"><span className="text-[10px] text-text-muted block">Hrs lost</span>
                  <input type="number" min={0} step="0.5" inputMode="decimal" value={dLabourHrs} onChange={e => setDLabourHrs(e.target.value)} className="w-full bg-ink border border-line rounded px-2 py-1 font-mono"/></label>
                <span className="pb-1.5 text-text-muted">=</span>
                <span className="pb-1 font-mono text-accent">{(parseFloat(dCrew)||0)*(parseFloat(dLabourHrs)||0) ? `${(parseFloat(dCrew)||0)*(parseFloat(dLabourHrs)||0)} man-hrs` : "—"}</span>
              </div>
              {/* Plant: qty × hours lost */}
              <div className="flex items-end gap-2 flex-wrap text-[12px]">
                <span className="w-12 text-text">Plant</span>
                <label className="w-[72px]"><span className="text-[10px] text-text-muted block">Qty</span>
                  <input type="number" min={0} inputMode="numeric" value={dPlantQty} onChange={e => setDPlantQty(e.target.value)} className="w-full bg-ink border border-line rounded px-2 py-1 font-mono"/></label>
                <span className="pb-1.5 text-text-muted">×</span>
                <label className="w-[72px]"><span className="text-[10px] text-text-muted block">Hrs lost</span>
                  <input type="number" min={0} step="0.5" inputMode="decimal" value={dPlantHrs} onChange={e => setDPlantHrs(e.target.value)} className="w-full bg-ink border border-line rounded px-2 py-1 font-mono"/></label>
                <span className="pb-1.5 text-text-muted">=</span>
                <span className="pb-1 font-mono text-accent">{(parseFloat(dPlantQty)||0)*(parseFloat(dPlantHrs)||0) ? `${(parseFloat(dPlantQty)||0)*(parseFloat(dPlantHrs)||0)} plant-hrs` : "—"}</span>
              </div>
              {/* TA: qty × nights */}
              <div className="flex items-end gap-2 flex-wrap text-[12px]">
                <span className="w-12 text-text">TA</span>
                <label className="w-[72px]"><span className="text-[10px] text-text-muted block">Qty</span>
                  <input type="number" min={0} inputMode="numeric" value={dTaQty} onChange={e => setDTaQty(e.target.value)} className="w-full bg-ink border border-line rounded px-2 py-1 font-mono"/></label>
                <span className="pb-1.5 text-text-muted">×</span>
                <label className="w-[72px]"><span className="text-[10px] text-text-muted block">Nights</span>
                  <input type="number" min={0} inputMode="numeric" value={dTaNights} onChange={e => setDTaNights(e.target.value)} className="w-full bg-ink border border-line rounded px-2 py-1 font-mono"/></label>
                <span className="pb-1.5 text-text-muted">=</span>
                <span className="pb-1 font-mono text-accent">{(parseFloat(dTaQty)||0)*(parseFloat(dTaNights)||0) ? `${(parseFloat(dTaQty)||0)*(parseFloat(dTaNights)||0)} TA nights` : "—"}</span>
              </div>
              <label className="flex items-center gap-2 text-[12px] text-text-muted pt-1">
                <input type="checkbox" checked={delayBillable} onChange={e => setDelayBillable(e.target.checked)} />
                Delay is billable (variation / EOT — flags for finance)
              </label>
            </div>
          )}
        </div>
      </Card>

      {/* Outage (collapsed by default) */}
      <Card className="mt-3">
        <button
          type="button"
          onClick={() => setOutageOpen(o => !o)}
          className="w-full p-4 flex items-center justify-between"
        >
          <div className="flex items-center gap-2">
            <AlertTriangle size={14} className="text-warn"/>
            <span className="text-[14px] font-semibold">Outage info</span>
            <span className="text-[11px] text-text-muted ml-1">— optional, if you took live equipment off</span>
          </div>
          {outageOpen ? <ChevronUp size={16}/> : <ChevronDown size={16}/>}
        </button>
        {outageOpen && (
          <div className="p-4 pt-0 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Carrier">
                <Select value={outageCarrier} onChange={e => { setOutageCarrier(e.target.value); setOutageType(""); }}>
                  <option value="">—</option>
                  {OUTAGE_CARRIERS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </Select>
              </Field>
              <Field label="Type">
                <Select value={outageType} onChange={e => setOutageType(e.target.value)} disabled={!outageCarrier}>
                  <option value="">—</option>
                  {(OUTAGE_TYPES_BY_CARRIER[outageCarrier] ?? []).map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </Select>
              </Field>
            </div>
            <Field label="Reference number (e.g. CMART-12345)">
              <Input value={outageRef} onChange={e => setOutageRef(e.target.value)} placeholder="—"/>
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Outage started">
                <Input type="datetime-local" value={outageStartedAt} onChange={e => setOutageStartedAt(e.target.value)} />
              </Field>
              <Field label="Outage ended">
                <Input type="datetime-local" value={outageEndedAt} onChange={e => setOutageEndedAt(e.target.value)} />
              </Field>
            </div>
            <Field label="Notes (optional)">
              <Textarea rows={2} value={outageNotes} onChange={e => setOutageNotes(e.target.value)}/>
            </Field>
          </div>
        )}
      </Card>

      {/* T-258 — Multi-photo. Uses DiaryPhotosField (extracted to keep this
          file under the truncation threshold). Up to 10 photos per entry, each
          with optional caption; reorder with up/down arrows; live preview. */}
      <Card className="mt-3">
        <div className="p-4">
          <SectionTitle icon={Camera}>Site photos</SectionTitle>
          <p className="text-[11px] text-text-muted mt-1 mb-3">
            How is site being left? Material stacks, fencing, signage, equipment positioning. Up to 10 photos — captions optional.
          </p>
          <DiaryPhotosField
            photos={stagedPhotos}
            onChange={setStagedPhotos}
            disabled={submitMut.isPending}
            label=""
            hint=""
          />
        </div>
      </Card>

      {/* Job finished tick */}
      <Card className="mt-3">
        <div className="p-4 flex items-center gap-3">
          <input
            id="job-finished"
            type="checkbox"
            checked={isJobFinished}
            onChange={e => setIsJobFinished(e.target.checked)}
            className="w-4 h-4"
          />
          <label htmlFor="job-finished" className="flex-1 cursor-pointer">
            <div className="text-[13px] font-medium inline-flex items-center gap-1.5">
              <FlagOff size={13}/> Job finished — this is the last diary entry
            </div>
            <div className="text-[11px] text-text-muted mt-0.5">
              Tick this to close out the build. Auto-stamps the project's actual end date.
            </div>
          </label>
        </div>
      </Card>

      {submitError && (
        <div className="mt-3 p-3 bg-lost/10 border border-lost/20 rounded text-[12px] text-lost flex items-start gap-2">
          <AlertTriangle size={14} className="mt-0.5 flex-shrink-0"/>
          {submitError}
        </div>
      )}

      {/* Sticky submit */}
      <div className="sticky bottom-4 max-md:bottom-[calc(var(--nav-clear)+1rem)] mt-4 z-10">
        <Card className="shadow-lg">
          <div className="p-3 flex items-center justify-between gap-3">
            <div className="text-[12px] text-text-muted">
              {!projectId ? "Pick a project to enable submit." :
                submitMut.isPending ? "Saving…" : "Ready to submit."}
            </div>
            <Button
              variant="primary"
              icon={Save}
              disabled={!projectId || submitMut.isPending}
              onClick={() => { setSubmitError(null); submitMut.mutate(); }}
            >
              {submitMut.isPending ? "Submitting…" : "Save entry"}
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Plant bookings near the diary day — James, Beechmont first live diary:
// "plant on site — why not, if available, link to a booking with contacts."
// Reads the project's plant bookings around the date (±3 days); tapping one
// prefills the plant fields and shows the contact. Free text stays for
// unbooked plant — this is a shortcut, not a gate.
// ---------------------------------------------------------------------------
function PlantBookingSuggest({ projectId, entryDate, onPick, pickedBookingIds = [] }: {
  projectId: string;
  entryDate: string;
  onPick: (p: { booking_id: string; type: string; supplier: string; operator: string }) => void;
  pickedBookingIds?: string[];
}) {
  const { data: bookings = [] } = useQuery({
    queryKey: ["diary_plant_bookings", projectId],
    enabled: !!projectId,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("project_bookings")
        .select("booking_id, title, booking_type, status, scheduled_start, scheduled_end, external_contact_name, external_contact_phone, suppliers(trading_name, legal_name, primary_contact_name, primary_contact_phone)")
        .eq("project_id", projectId)
        .eq("booking_type", "plant")
        .neq("status", "cancelled");
      if (error) throw error;
      return (data ?? []) as any[];
    },
  });

  const near = bookings.filter((b) => {
    const s = String(b.scheduled_start).slice(0, 10);
    const e = String(b.scheduled_end ?? b.scheduled_start).slice(0, 10);
    const lo = new Date(entryDate + "T00:00:00"); lo.setDate(lo.getDate() - 3);
    const hi = new Date(entryDate + "T00:00:00"); hi.setDate(hi.getDate() + 3);
    const loK = format(lo, "yyyy-MM-dd"), hiK = format(hi, "yyyy-MM-dd");
    return s <= hiK && e >= loK;
  }).slice(0, 8);

  if (!near.length) return null;

  return (
    <div className="mt-3 space-y-1.5">
      <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-mono">
        Booked on the schedule — tap each one that was on site
      </div>
      {near.map((b) => {
        const sup = b.suppliers as any;
        const supplierName = sup?.trading_name ?? sup?.legal_name ?? b.external_contact_name ?? "";
        const contactName = b.external_contact_name ?? sup?.primary_contact_name ?? "";
        const contactPhone = b.external_contact_phone ?? sup?.primary_contact_phone ?? "";
        const picked = pickedBookingIds.includes(b.booking_id);
        return (
          <button
            key={b.booking_id} type="button"
            onClick={() => onPick({ booking_id: b.booking_id, type: b.title, supplier: supplierName, operator: contactName })}
            className={`w-full text-left rounded-md border px-3 py-2 transition-colors ${picked ? "border-accent bg-accent/10" : "border-line bg-ink-raised hover:border-accent/50"}`}
          >
            <div className="text-[12.5px] font-medium flex items-center gap-1.5">{picked && <Check size={12} className="text-accent" />}{b.title}</div>
            <div className="text-[11px] text-text-muted">
              {new Date(b.scheduled_start).toLocaleDateString("en-AU", { day: "numeric", month: "short" })}
              {supplierName && <> · {supplierName}</>}
              {contactName && <> · {contactName}</>}
              {contactPhone && <> · <span className="text-accent">{contactPhone}</span></>}
            </div>
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Today's build plan — mig 358 tie-in. James, 8 Sep: "when I do [the site
// diary] … the complete that days sub activities would be good."
// Shows the program lines whose dates cover the diary day, with their
// sub-activity tick-offs INLINE — ticking here is the same record as the
// Program panel and the crew build plan (project_program_subactivities.done,
// who/when stamped by the mig-345 trigger). Nothing extra is stored on the
// diary; the program IS the record.
// ---------------------------------------------------------------------------
function TodaysBuildPlan({ projectId, entryDate, canManage }: { projectId: string; entryDate: string; canManage: boolean }) {
  const qc = useQueryClient();
  const [showBare, setShowBare] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: ["diary_program", projectId],
    enabled: !!projectId,
    queryFn: async () => {
      const [acts, subs] = await Promise.all([
        (supabase as any).from("project_program_activities")
          .select("activity_id, code, name, kind, start_date, end_date, track_progress, done, is_active, crew_done_at, crew_done_by")
          .eq("project_id", projectId).eq("is_active", true).order("sort_order"),
        (supabase as any).from("project_program_subactivities")
          .select("sub_id, activity_id, name, sort_order, done, done_at")
          .eq("project_id", projectId).order("sort_order"),
      ]);
      if (acts.error) throw acts.error;
      if (subs.error) throw subs.error;
      return { acts: acts.data ?? [], subs: subs.data ?? [] };
    },
  });

  async function toggleSub(su: any) {
    const { error } = await (supabase as any)
      .from("project_program_subactivities")
      .update({ done: !su.done }).eq("sub_id", su.sub_id);
    if (error) { alert(`Couldn't save the tick: ${error.message}`); return; }
    qc.invalidateQueries({ queryKey: ["diary_program", projectId] });
  }

  if (!projectId) return null;
  if (isLoading) return <div className="py-2"><Spinner size={14} /></div>;

  const subsByAct = new Map<string, any[]>();
  for (const s of data?.subs ?? []) {
    if (!subsByAct.has(s.activity_id)) subsByAct.set(s.activity_id, []);
    subsByAct.get(s.activity_id)!.push(s);
  }
  // James, Beechmont live diary v2 feedback: "too many lines here that you
  // can't tick … the first available tick box is way down." So: only lines
  // with something ACTIONABLE show by default — lines with sub-activity
  // tick-offs float to the top (today's planned ones first). Bare lines
  // collapse into one row; expanding them gives managers a one-click
  // "mark line complete" each (line writes are manager-only under RLS,
  // so field users just see the count).
  const open = (data?.acts ?? []).filter((a: any) => a.track_progress && !a.done);
  const coversDay = (a: any) =>
    a.start_date && a.start_date <= entryDate && (a.end_date ?? a.start_date) >= entryDate;
  const withSubs = open
    .filter((a: any) => (subsByAct.get(a.activity_id) ?? []).length > 0)
    .sort((x: any, y: any) => Number(coversDay(y)) - Number(coversDay(x)));
  const bare = open.filter((a: any) => (subsByAct.get(a.activity_id) ?? []).length === 0);

  if (!(data?.acts ?? []).length) {
    return (
      <div className="text-[11px] text-text-dim">
        No build program on this project yet — lines set up on the Program tab appear here to tick off.
      </div>
    );
  }
  if (!open.length) {
    return (
      <div className="text-[11px] text-emerald-700">
        Every tracked line on the build program is complete.
      </div>
    );
  }

  async function markLineDone(a: any) {
    const { error } = await (supabase as any)
      .from("project_program_activities")
      .update({ done: true }).eq("activity_id", a.activity_id);
    if (error) { alert(`Couldn't mark the line: ${error.message}`); return; }
    qc.invalidateQueries({ queryKey: ["diary_program", projectId] });
  }

  // Mig 402 — the CREW's tick on a bare line (James, 29 Sep, Tamborine: "12
  // lines listed, nothing tickable"). Their claim, waiting on the manager's
  // confirm; goes through the RPC because the table's update is manager-only.
  async function crewTick(a: any, done: boolean) {
    const { error } = await (supabase as any).schema("app")
      .rpc("program_line_crew_tick", { p_activity_id: a.activity_id, p_done: done });
    if (error) { alert(`Couldn't save the tick: ${error.message}`); return; }
    qc.invalidateQueries({ queryKey: ["diary_program", projectId] });
  }

  return (
    <div className="rounded-md border border-line bg-ink-raised p-3 space-y-2">
      <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-mono">
        Build plan — tick off what got done
      </div>
      {withSubs.length === 0 && bare.length === 0 && (
        <div className="text-[11px] text-text-muted">
          No lines have tick-off steps yet — sub-activities added on the Program tab land here.
        </div>
      )}
      {withSubs.map((a: any) => {
        const subs = subsByAct.get(a.activity_id) ?? [];
        const doneN = subs.filter((s) => s.done).length;
        return (
          <div key={a.activity_id}>
            <div className="flex items-center gap-2 text-[12.5px]">
              <span className="font-medium">{a.name}</span>
              {coversDay(a) && (
                <span className="text-[9px] px-1 rounded border border-accent/40 text-accent">today</span>
              )}
              <span className={`text-[9px] px-1 rounded border tabular-nums ${doneN === subs.length ? "border-emerald-500/50 text-emerald-700 bg-emerald-500/10" : "border-line text-text-muted"}`}>
                ☑ {doneN}/{subs.length}
              </span>
            </div>
            <div className="mt-1 space-y-1 pl-1">
              {subs.map((su) => (
                <label key={su.sub_id} className="flex items-center gap-2 text-[12px] cursor-pointer select-none">
                  <input type="checkbox" checked={su.done} onChange={() => void toggleSub(su)} className="accent-accent" />
                  <span className={su.done ? "line-through text-text-dim" : "text-text-muted"}>{su.name}</span>
                  {su.done && su.done_at && (
                    <span className="text-[10px] text-emerald-700">
                      {new Date(su.done_at).toLocaleDateString("en-AU", { day: "numeric", month: "short" })}
                    </span>
                  )}
                </label>
              ))}
            </div>
          </div>
        );
      })}
      {/* Mig 402 — bare lines (no tick-off steps) are TICKABLE by everyone now.
          The crew's tick is their claim ("we did this"); a manager's tick is the
          confirm that the gantt and the client see. Today's lines and lines the
          crew has ticked are shown open; the rest fold behind one row so the
          first box is never "way down" (Beechmont feedback). */}
      {bare.length > 0 && (() => {
        const bareToday = bare.filter((a: any) => coversDay(a) || a.crew_done_at);
        const bareRest  = bare.filter((a: any) => !(coversDay(a) || a.crew_done_at));
        const row = (a: any) => {
          const claimed = !!a.crew_done_at;
          return (
            <div key={a.activity_id} className="flex items-center gap-2 text-[12px]">
              {canManage ? (
                <input type="checkbox" checked={false} onChange={() => void markLineDone(a)}
                  className="accent-accent cursor-pointer"
                  title={claimed ? "The crew ticked this — tick to confirm it complete" : "Mark this whole line complete"} />
              ) : (
                <input type="checkbox" checked={claimed} onChange={() => void crewTick(a, !claimed)}
                  className="accent-accent cursor-pointer"
                  title={claimed ? "You ticked this — untick if it isn't done" : "Tick when this line is done"} />
              )}
              <span className={claimed ? "text-text line-through decoration-amber-500/70" : "text-text-muted"}>{a.name}</span>
              {coversDay(a) && (
                <span className="text-[9px] px-1 rounded border border-accent/40 text-accent">today</span>
              )}
              {claimed && (
                <span className="text-[9px] px-1 rounded border border-amber-500/50 text-amber-600 bg-amber-500/10"
                  title={`Ticked by the crew ${new Date(a.crew_done_at).toLocaleDateString("en-AU", { day: "numeric", month: "short" })}${canManage ? " — tick the box to confirm" : " — manager to confirm"}`}>
                  {canManage ? "crew says done · confirm" : "done · manager to confirm"}
                </span>
              )}
            </div>
          );
        };
        return (
          <div className="space-y-1 pl-1">
            {bareToday.map(row)}
            {bareRest.length > 0 && (
              <div>
                <button type="button" onClick={() => setShowBare(!showBare)}
                  className="text-[11px] text-accent hover:underline">
                  {showBare
                    ? "Hide the other lines"
                    : `${bareRest.length} more open line${bareRest.length === 1 ? "" : "s"} (not planned for today) ▸`}
                </button>
                {showBare && <div className="mt-1.5 space-y-1">{bareRest.map(row)}</div>}
              </div>
            )}
            {!canManage && (
              <div className="text-[10.5px] text-text-dim">
                Your tick tells the office the line is done; a manager confirms it on the program.
              </div>
            )}
          </div>
        );
      })()}
      <div className="text-[10.5px] text-text-dim">
        Ticks save straight to the build program — same record the Program tab and the crew build plan show.
      </div>
    </div>
  );
}

function SectionTitle({ children, icon: Icon }: { children: React.ReactNode; icon?: any }) {
  return (
    <div className="text-[11px] font-semibold text-text-muted uppercase tracking-wider2 inline-flex items-center gap-1.5">
      {Icon && <Icon size={11}/>}
      {children}
    </div>
  );
}
